// @flow
import moment from "moment"

import { DATE_FORMAT } from "../../consts/date"

export function isDate(
  date: ?string,
  format: string | Array<string> = DATE_FORMAT,
  strict: boolean = false,
): boolean {
  return moment(date, format, strict).isValid()
}

export function isDateWithAlternatives(
  value: ?string,
  format: string | Array<string> = DATE_FORMAT,
  strict: boolean = false,
  ...alternatives: Array<string>
): boolean {
  return value ? alternatives.includes(value) || isDate(value, format, strict) : false
}

export function isBirthday(
  date: ?string,
  format: string | Array<string> = DATE_FORMAT,
  strict: boolean = false,
): boolean {
  const birthday = moment(date, format, strict)

  return (
    birthday.isValid() && birthday.isBefore(moment()) && birthday.isAfter(moment({ year: 1900 }))
  )
}

export function isDocumentAfter(
  expirationDate: string,
  minDate: string,
  alternativeValue: string | false = false,
  format: string | Array<string> = DATE_FORMAT,
  strict: boolean = false,
): boolean {
  const expiration = moment(expirationDate, format, strict)
  const lastArrival = moment(minDate, format, strict)
  return expirationDate === alternativeValue || lastArrival.isSameOrBefore(expiration)
}

export function isAdult(
  date: ?string,
  format: string | Array<string> = DATE_FORMAT,
  limit: number,
): boolean {
  const birthday = moment(date, format)

  return isBirthday(date, format) && moment().subtract(limit, "years") > birthday
}

export function isName(name: string): boolean {
  return name.trim().length >= 2
}

export function isNameTooLong(name: string): boolean {
  return name.trim().length > 50
}

export function isPhone(phone: ?string): boolean {
  return phone ? !/[^0-9\s+\-_]+/.test(phone) : false
}

export function isInternationalPhone(phone: ?string): boolean {
  return phone
    ? /(([+][(]?[0-9]{1,3}[)]?)|([(]?[0-9]{4}[)]?))\s*[)]?[-\s\.]?[(]?[0-9]{1,3}[)]?([-\s\.]?[0-9]{3})([-\s\.]?[0-9]{3,4})/.test(
        phone,
      )
    : false
}

// allows '12.' or '2,'
export function isDecimal(number: ?string): boolean {
  return number ? /^[0-9]+[,.]?([0-9]{1,2})?$/.test(number) : false
}

export function isPrice(price: string): boolean {
  return /^[0-9]+([٫,.][0-9]*)?$/.test(price)
}

// TODO: Incestigate commented reservation-contact assertions and fix the validator
export function isEmail(input: string): boolean {
  const re = /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/i
  return re.test(input)
}

export function isUserId(input: string): boolean {
  let check = false
  if (typeof input === "string") {
    if (input.length === 32 || input.length === 36) {
      if (/^[\w\d-]+$/.test(input)) {
        check = true
      }
    }
  }

  return check
}

export function isBookingId(input: number) {
  return typeof input === "number" && input.toString().length > 4
}

export function isBookingToken(input: string) {
  return typeof input === "string" && input.length > 20
}

export function isLatin(input: string) {
  // \u0021 !
  // \u0022 "
  // \u0023 #
  // \u0024 $
  // \u0025 %
  // \u0026 &
  // \u0027 ' allowed
  // \u0028 (
  // \u0029 )
  // \u002a *
  // \u002b +
  // \u002c ,
  // \u002d - allowed
  // \u002e .
  // \u002f /
  // \u0030 0
  // \u0031 1
  // \u0032 2
  // \u0033 3
  // \u0034 4
  // \u0035 5
  // \u0036 6
  // \u0037 7
  // \u0038 8
  // \u0039 9
  // \u003a :
  // \u003b ;
  // \u003c <
  // \u003d =
  // \u003e >
  // \u003f ?
  // \u0040 @
  if (/[\u0021-\u0026\u0028-\u002c\u002e-\u0040]/.test(input)) return false

  // InBasic_Latin       : "0000-007F",
  // InLatin_1_Supplement: "0080-00FF",
  // InLatin_Extended_A  : "0100-017F",
  // InLatin_Extended_B  : "0180-024F",
  return new RegExp(/^[\u0000-\u007F\u0080-\u00FF\u0100-\u017F\u0180-\u024F]+$/).test(input) // eslint-disable-line
}

export function isCyrillic(input: string) {
  // no punctuation and numbers allowed
  if (/[\u0021-\u0026\u0028-\u002c\u002e-\u0040]/.test(input)) return false
  // Basic_Cyrillic: "0400-04FF",
  return new RegExp(/^[\u0400-\u04FF]+$/).test(input) // eslint-disable-line
}

export function isWeakLatin(input: string) {
  // ignores basic keyboard punctuation and numbers
  return isLatin(input.replace(/[0-9\u0021-\u0040\u2013-\u2015\u2018-\u201F\u2026]/g, ""))
}

export function isSwift(input: string): boolean {
  return Boolean(input && input.match(/^([\w0-9]{8}|[\w0-9]{11})$/))
}

// TODO: Replace with real IBAN check, eg. https://github.com/arhs/iban.js
export function isIBAN(input: string): boolean {
  return Boolean(input && input.match(/^[\w\d ]{5,34}$/))
}

export function isIATA(input: string): boolean {
  return Boolean(input && input.match(/^[A-Za-z]{3}$/))
}

export function isIdNumber(input: string): boolean {
  return Boolean(input && input.length >= 5)
}

export function isNotTooLong(input: string): boolean {
  return input.trim().length <= 20
}

export function isCompanyId(input: string): boolean {
  return Boolean(input && input.trim().length >= 3)
}

export function isWord(input: string): boolean {
  return !/[{}_~!"#\\$%&'()*+,\\ -\\./:;<=>?@]/.test(input)
}

export function isSlug(input: string): boolean {
  return /^[^{}~!"#\\$%&'()*+,./:;<=>?@0-9 ]+(-[0-9])?$/.test(input)
}
